import { useState } from 'react';

import { ClockFastForwardIcon, EyeIcon, EyeOffIcon } from '@/assets/icons';
import { Badge, Button, SuspenseImage } from '@/components/common';
import {
  FaceIdRegistrationStatus,
  getFaceIdRegistrationStatusLabel,
  getProfileStatusLabel,
  getProfileStatusVariant,
  getProfileTypeLabel,
  IFaceIdProfileDetailsCard,
  ProfileType,
} from '@/features/face-id-profiles';
import { formatDate, getDate } from '@/utils';
import { useFaceIdFacialDataQuery } from '@/features/face-id-profiles/hooks';

const FaceIdProfileDetailsCard = ({ profile }: IFaceIdProfileDetailsCard) => {
  const [isFaceIdHidden, setFaceIdHiddenValue] = useState(true);
  const { data: facialData } = useFaceIdFacialDataQuery(
    profile.profileId,
    profile.faceRegistrationStatus,
  );

  return (
    <div className="rounded-softer border border-neutral-n4 bg-neutral-white">
      <div className="text-sm flex flex-col gap-y-base-plus p-m">
        {/* ID */}
        <div className="flex gap-x-xs">
          <div className="font-semibold text-brand-primary-darkest w-[120px] sm:w-[250px]">ID:</div>
          <div className="font-medium text-neutral-n8 flex-1 break-all">{profile.profileId}</div>
        </div>

        {/* Full Name */}
        <div className="flex gap-x-xs">
          <div className="font-semibold text-brand-primary-darkest w-[120px] sm:w-[250px]">
            Full Name:
          </div>
          <div className="font-medium text-neutral-n8 flex-1 break-all">{profile.fullName}</div>
        </div>

        {/* Profile Type */}
        <div className="flex gap-x-xs">
          <div className="font-semibold text-brand-primary-darkest w-[120px] sm:w-[250px]">
            Profile Type:
          </div>
          <div className="font-medium text-neutral-n8 flex-1 break-all">
            {getProfileTypeLabel(profile.profileType)}
          </div>
        </div>

        {/* Status */}
        <div className="flex gap-x-xs items-center">
          <div className="font-semibold text-brand-primary-darkest w-[120px] sm:w-[250px]">
            Status:
          </div>
          <Badge className="border-none" variant={getProfileStatusVariant(profile.status)}>
            {getProfileStatusLabel(profile.status)}
          </Badge>
        </div>

        {profile.profileType === ProfileType.Employee && (
          <>
            {/* Employee ID */}
            <div className="flex gap-x-xs">
              <div className="font-semibold text-brand-primary-darkest w-[120px] sm:w-[250px]">
                Employee ID:
              </div>
              <div className="font-medium text-neutral-n8 flex-1 break-all">
                {profile.employeeId}
              </div>
            </div>

            {/* Department */}
            <div className="flex gap-x-xs">
              <div className="font-semibold text-brand-primary-darkest w-[120px] sm:w-[250px]">
                Department/Team:
              </div>
              <div className="font-medium text-neutral-n8 flex-1 break-all">
                {profile.department}
              </div>
            </div>

            {/* Email */}
            <div className="flex gap-x-xs">
              <div className="font-semibold text-brand-primary-darkest w-[120px] sm:w-[250px]">
                Email:
              </div>
              <div className="font-medium text-neutral-n8 flex-1 break-all">{profile.email}</div>
            </div>

            {/* Profile Photo */}
            <div className="flex gap-x-xs">
              <div className="font-semibold text-brand-primary-darkest w-[120px] sm:w-[250px]">
                Profile Photo:
              </div>
              <SuspenseImage
                src={profile.photoUrl}
                alt="Profile"
                className="h-40 w-[120px] object-cover"
              />
            </div>
          </>
        )}
        {profile.profileType === ProfileType.Guest && (
          <>
            <div className="flex gap-x-xs">
              <div className="font-semibold text-brand-primary-darkest w-[120px] sm:w-[250px]">
                Guest Valid Period:
              </div>
              <div className="font-medium text-neutral-n8 flex-1">
                {formatDate({ date: getDate(profile.guestValidFrom) })} -&nbsp;
                {formatDate({ date: getDate(profile.guestValidTo) })}
              </div>
            </div>
          </>
        )}

        {/* Face ID Registration */}
        <div className="flex gap-x-xs items-center">
          <div className="font-semibold text-brand-primary-darkest w-[120px] sm:w-[250px]">
            Face ID Registration:
          </div>
          {profile.faceRegistrationStatus === FaceIdRegistrationStatus.Pending ? (
            <Badge variant="pending">
              <ClockFastForwardIcon className="size-base" />
              {getFaceIdRegistrationStatusLabel(profile.faceRegistrationStatus)}
            </Badge>
          ) : isFaceIdHidden ? (
            <Button
              variant="secondary"
              borderRadius="soft"
              prefixIcon={<EyeIcon />}
              onClick={() => setFaceIdHiddenValue(false)}
            >
              View Face ID
            </Button>
          ) : (
            <Button
              variant="secondary"
              borderRadius="soft"
              prefixIcon={<EyeOffIcon />}
              onClick={() => setFaceIdHiddenValue(true)}
            >
              Hide Face ID
            </Button>
          )}
        </div>

        {/* Registration Photos */}
        {!isFaceIdHidden &&
          profile.faceRegistrationStatus === FaceIdRegistrationStatus.Registered && (
            <div className="flex gap-x-xs">
              <div className="font-semibold text-brand-primary-darkest w-[120px] sm:w-[250px]"></div>
              <div className="flex flex-1 gap-xs overflow-x-auto [scrollbar-width:thin]">
                {Object.keys(facialData?.facialData || {}).map((key) => (
                  <SuspenseImage
                    key={key}
                    src={facialData?.facialData[key as keyof typeof facialData.facialData]}
                    alt={`Registration ${key}`}
                    className="h-40 w-[120px] object-cover"
                  />
                ))}
              </div>
            </div>
          )}

        {/* Remark */}
        <div className="flex gap-x-xs">
          <div className="font-semibold text-brand-primary-darkest w-[120px] sm:w-[250px]">
            Remark:
          </div>
          <div className="font-medium text-neutral-n8 flex-1 break-all">
            {profile.remarks ? profile.remarks : <span className="tracking-[1px]">--</span>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FaceIdProfileDetailsCard;
